import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, Sparkles } from "lucide-react";
import { SearchPanel } from "@/components/SearchPanel";
import { FiltersBar } from "@/components/FiltersBar";
import { TonightsPicks } from "@/components/TonightsPicks";
import { VenueCard } from "@/components/VenueCard";
import { ResultsState } from "@/components/ResultsState";
import { useSearch } from "@/context/SearchContext";
import { useNow } from "@/hooks/useNow";
import { applyFilters, todayKey } from "@/lib/hh";

function Hero() {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-zinc-800">
      <img src="/hero-bar.jpg" alt="Dim cocktail bar at night" className="absolute inset-0 h-full w-full object-cover object-center opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
      <div className="relative px-6 py-12 sm:px-10 sm:py-16">
        <p className="eyebrow">Free · Within 60 miles · Updated by owners</p>
        <h1 className="font-heading mt-3 max-w-2xl text-5xl leading-[0.95] text-white sm:text-6xl lg:text-7xl">
          Cheap drinks. Good company. <span className="text-[#fde68a]">Right now.</span>
        </h1>
        <p className="mt-4 max-w-lg text-zinc-300">Find happy hour deals at bars and restaurants near you, see what's pouring tonight, and plan a crawl with friends.</p>
      </div>
    </section>
  );
}

export default function Home() {
  const { venues, status } = useSearch();
  const now = useNow(30000);
  const [filters, setFilters] = useState({ day: todayKey(), openNow: false, endingSoon: false, under5: false, sort: "distance" });
  const list = useMemo(() => applyFilters(venues, filters, now), [venues, filters, now]);
  const ready = venues.length > 0;
  return (
    <div className="page">
      <Hero />
      <div className="relative z-10 -mt-6 px-2 sm:px-6"><SearchPanel /></div>
      {ready && <TonightsPicks venues={venues} />}
      <section className="mt-10">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-heading text-3xl text-white sm:text-4xl">Happy Hours Near You</h2>
          {ready && (
            <Link to="/map" data-testid="view-map-link" className="flex items-center gap-2 text-sm font-semibold text-[#fde68a] hover:underline">
              <MapIcon size={16} /> View on map
            </Link>
          )}
        </div>
        {ready && <FiltersBar filters={filters} setFilters={setFilters} count={list.length} />}
        {status === "loading" && ready && <p className="mt-3 text-sm text-zinc-500">Still finding more spots…</p>}
        <div className="mt-5"><ResultsState empty={ready && !list.length} /></div>
        {status === "error" && ready && !list.length && <p className="mt-3 text-sm text-zinc-500">No spots match these filters.</p>}
        <div data-testid="venue-list" className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((v) => <VenueCard key={v.id} venue={v} day={filters.day} />)}
        </div>
        {ready && (
          <p className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
            <Sparkles size={13} /> Listings are found by AI and from bar owners. Specials and times can change, so confirm with the venue.
          </p>
        )}
      </section>
    </div>
  );
}
